import React, { LegacyRef } from "react";

import { GridColumnType } from "./GridColumn";
import Checkbox from "../inputform/Checkbox";
import Textbox from "../inputform/Textbox";
import Button from "../buttons/Button";
import DropDown, { DropDownOption } from "../inputform/DropDown";
import Utility from "../../foundation/Utility";

interface GridEditorProps {
    type?: GridColumnType;
    icon?: string;
    value?: any;
    blank?: boolean;
    editable?: boolean;
    minNumber?: number;
    maxNumber?: number;
    allowNull?: boolean;
    naForNull?: boolean;
    decimalPlaces?: number;
    decimalIncrement?: number;
    options?: DropDownOption[];
    valueEdited?: (value: any) => void;
    onClick?: () => void;
    onKeyDown?: (event: KeyboardEvent) => void;
    onKeyUp?: (event: KeyboardEvent) => void;
    prevRecordRequested?: () => void;
    nextRecordRequested?: () => void;
    prevColumnRequested?: () => void;
    nextColumnRequested?: () => void;
}

interface GridEditorState {
    value?: any;
    ref:
        | LegacyRef<Checkbox>
        | LegacyRef<Textbox>
        | LegacyRef<Button>
        | LegacyRef<DropDown>;
    keepOpen: boolean;
}

class GridEditor extends React.Component<GridEditorProps, GridEditorState> {
    state: GridEditorState = {
        ref: null,
        value: null,
        keepOpen: true,
    };

    static getDerivedStateFromProps(
        props: GridEditorProps,
        state: GridEditorState,
    ): GridEditorState {
        let ref:
            | LegacyRef<Checkbox>
            | LegacyRef<Textbox>
            | LegacyRef<Button>
            | LegacyRef<DropDown>;

        switch (props.type ?? typeof props.value) {
            case GridColumnType.Checkbox:
                ref = React.createRef<Checkbox>();
                break;
            case GridColumnType.Number:
                ref = React.createRef<Textbox>();
                break;
            case GridColumnType.Button:
                ref = React.createRef<Button>();
                break;
            case GridColumnType.Dropdown:
                ref = React.createRef<DropDown>();
                break;
            case "boolean":
                ref = React.createRef<Checkbox>();
                break;
            case "number":
                ref = React.createRef<Textbox>();
                break;
            default:
                ref = React.createRef<Textbox>();
        }

        return {
            ...state,
            ref: ref,
        };
    }

    componentDidMount(): void {
        this.setState(() => ({ value: this.props.value, keepOpen: true }));

        if (typeof this.props.value !== "boolean") {
            setTimeout(this.doFocus.bind(this), 0);
        }
    }
    componentWillUnmount(): void {
        if (
            this.props.valueEdited &&
            this.state.value &&
            this.state.value !== this.props.value
        )
            this.props.valueEdited(this.state.value);
    }

    inputChanged(value: any) {
        this.setState(() => ({ value: value, keepOpen: false }));
        this.inputChangedBubble(value);
    }
    inputChangedBubble = Utility.debounce((value: any) => {
        if (value !== this.props.value) this.props.valueEdited?.(value);
    }, 500);
    keyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        if (this.props.onKeyDown) this.props.onKeyDown(event.nativeEvent);
    }
    keyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        if (this.props.onKeyUp) this.props.onKeyUp(event.nativeEvent);
        switch (event.key) {
            case "Escape":
                this.setState(() => ({ value: this.props.value }));
                break;
            case "ArrowUp":
                if (this.props.prevRecordRequested)
                    this.props.prevRecordRequested();
                event.stopPropagation();
                event.preventDefault();
                break;
            case "Enter":
            case "ArrowDown":
                if (this.props.nextRecordRequested)
                    this.props.nextRecordRequested();
                event.stopPropagation();
                event.preventDefault();
                break;
            case "ArrowLeft":
                if (this.props.prevColumnRequested)
                    this.props.prevColumnRequested();
                event.stopPropagation();
                event.preventDefault();
                break;
            case "ArrowRight":
                if (this.props.nextColumnRequested)
                    this.props.nextColumnRequested();
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    }

    getEditor() {
        switch (this.props.type ?? typeof this.props.value) {
            case GridColumnType.Checkbox:
                return (
                    <Checkbox
                        ref={this.state.ref as LegacyRef<Checkbox>}
                        value={this.state.value ?? false}
                        disabled={!this.props.editable}
                        onChange={value => this.inputChanged(value)}
                    />
                );
            case GridColumnType.Number:
                return (
                    <Textbox
                        type="number"
                        ref={this.state.ref as LegacyRef<Textbox>}
                        value={this.state.value ?? ""}
                        disabled={!this.props.editable}
                        allowNull={this.props.allowNull}
                        naForNull={this.props.naForNull}
                        decimalPlaces={this.props.decimalPlaces}
                        decimalIncrement={this.props.decimalIncrement}
                        min={this.props.minNumber}
                        max={this.props.maxNumber}
                        onChange={value => this.inputChanged(value)}
                        onKeyDown={this.keyDown.bind(this)}
                        onKeyUp={this.keyUp.bind(this)}
                    />
                );
            case GridColumnType.Icon:
                return <i>{this.props.icon}</i>;
            case GridColumnType.Button:
                return (
                    <Button
                        ref={this.state.ref as LegacyRef<Button>}
                        icon={this.props.icon}
                        text={this.state.value ?? ""}
                        disabled={!this.props.editable}
                        onClick={this.props.onClick}
                    />
                );
            case GridColumnType.Dropdown:
                return (
                    <DropDown
                        noType={true}
                        blank={this.props.blank}
                        focusOpen={true}
                        ref={this.state.ref as LegacyRef<DropDown>}
                        value={this.state.value ?? ""}
                        disabled={!this.props.editable}
                        onChange={value => this.inputChanged(value)}
                        options={this.props.options ?? []}
                    />
                );
            case "boolean":
                return (
                    <Checkbox
                        value={this.state.value ?? false}
                        disabled={!this.props.editable}
                        onChange={value => this.inputChanged(value)}
                    />
                );
            case "number":
                return (
                    <Textbox
                        type="number"
                        ref={this.state.ref as LegacyRef<Textbox>}
                        value={this.state.value ?? ""}
                        disabled={!this.props.editable}
                        onChange={value => this.inputChanged(value)}
                        onKeyDown={this.keyDown.bind(this)}
                        onKeyUp={this.keyUp.bind(this)}
                    />
                );
            default:
                return (
                    <Textbox
                        ref={this.state.ref as LegacyRef<Textbox>}
                        value={this.state.value ?? ""}
                        disabled={!this.props.editable}
                        onChange={value => this.inputChanged(value)}
                        onKeyDown={this.keyDown.bind(this)}
                        onKeyUp={this.keyUp.bind(this)}
                    />
                );
        }
    }

    doFocus() {
        switch (this.props.type ?? typeof this.props.value) {
            case GridColumnType.Checkbox:
                (this.state.ref as React.RefObject<Checkbox>)?.current?.focus();
                break;
            case GridColumnType.Number:
                (this.state.ref as React.RefObject<Textbox>)?.current?.focus();
                break;
            case GridColumnType.Button:
                (this.state.ref as React.RefObject<Button>)?.current?.focus();
                break;
            case GridColumnType.Dropdown:
                (this.state.ref as React.RefObject<DropDown>)?.current?.focus();
                break;
            case "boolean":
                (this.state.ref as React.RefObject<Checkbox>)?.current?.focus();
                break;
            case "number":
                (this.state.ref as React.RefObject<Textbox>)?.current?.focus();
                break;
            default:
                (this.state.ref as React.RefObject<Textbox>)?.current?.focus();
        }
    }

    render(): React.ReactNode {
        return this.getEditor();
    }
}

export default GridEditor;
